import { useState, useEffect } from 'react';
import { coreAPI } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface SidebarVersionProps {
  /** The sidebar is collapsed: render nothing (the version shows only when expanded). */
  collapsed: boolean;
  /** Helix internal users get a hover with the build commit and build time. */
  showBuild: boolean;
}

interface BuildDetails {
  version: string;
  gitCommit: string | null;
  builtAt: string | null;
}

/**
 * Release number at the foot of the sidebar, e.g. "v1.182.279".
 *
 * Everyone sees the release number from the manual version bump. Helix
 * internal users also get a native tooltip naming the commit that is actually
 * deployed and when it was built (CR-8222), because the release number does
 * not move with every merge.
 *
 * Mounted even while the sidebar is collapsed so the version is fetched once
 * per page load, as before, rather than on every expand.
 *
 * @param collapsed - Hide the version (sidebar collapsed)
 * @param showBuild - Add the build tooltip (Helix internal users only)
 * @returns The version line, or null while collapsed
 * @link Sidebar
 */
export default function SidebarVersion({ collapsed, showBuild }: SidebarVersionProps) {
  const [details, setDetails] = useState<BuildDetails>({ version: '1.0.0', gitCommit: null, builtAt: null });
  const { formatDateTime } = useFormatters();

  useEffect(() => {
    coreAPI.getVersion()
      .then((res) => setDetails({
        version: res.data.version,
        gitCommit: res.data.git_commit || null,
        builtAt: res.data.built_at ?? null,
      }))
      .catch(() => {
        // Keep the default version on error
      });
  }, []);

  if (collapsed) return null;

  // No tooltip until the version has loaded, so nobody reads a placeholder
  // build as the deployed one.
  const title = showBuild && details.gitCommit
    ? [
      `Build ${details.gitCommit}`,
      details.builtAt ? `Built ${formatDateTime(details.builtAt)}` : null,
    ].filter(Boolean).join('\n')
    : undefined;

  return (
    <div className="mt-3 pt-3 border-t border-border">
      <span title={title} className="block text-xs text-muted-foreground w-full text-left px-3">
        v{details.version}
      </span>
    </div>
  );
}
